<script setup lang="ts">
import { ref } from 'vue';
import { vClickOutside } from '@src/shared/lib/directives/clickOutside';

export type MobileSelectOption = {
	iconUrl?: string;
	label: string;
	value: string;
};

defineProps<{
	iconUrl?: string;
	label: string;
	modelValue: string;
	options: MobileSelectOption[];
}>();

const emit = defineEmits<{
	select: [value: string];
}>();

const isOpen = ref(false);

function toggleDropdown() {
	isOpen.value = !isOpen.value;
}

function closeDropdown() {
	isOpen.value = false;
}

function onSelect(value: string) {
	closeDropdown();
	emit('select', value);
}
</script>

<template>
	<div v-click-outside="closeDropdown" class="relative" @keydown.esc="closeDropdown">
		<button
			type="button"
			class="flex h-12 w-full cursor-pointer items-center justify-between rounded-xl border border-cardBorder bg-cardBg px-4 text-sm transition-colors hover:border-cardBorderHover"
			aria-haspopup="listbox"
			:aria-expanded="isOpen"
			@click="toggleDropdown"
		>
			<span class="flex min-w-0 items-center gap-2">
				<img v-if="iconUrl" :src="iconUrl" :alt="label" class="size-5 shrink-0" />
				<span class="truncate">{{ label }}</span>
			</span>
			<img
				src="/src/shared/assets/icons/fill-arrow-dropdown.svg"
				class="size-2 shrink-0 transition-transform duration-200"
				:class="isOpen ? 'rotate-180' : 'rotate-0'"
				alt=""
			/>
		</button>

		<Transition name="dropdown">
			<ul
				v-if="isOpen"
				class="absolute inset-x-0 bottom-full z-10 mb-2 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-xl border border-cardBorder bg-mainBg p-2 shadow-lg shadow-black/40"
			>
				<li v-for="option in options" :key="option.value">
					<button
						type="button"
						class="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-white/10"
						:class="option.value === modelValue ? 'bg-white/10 text-primaryText' : 'text-secondaryText'"
						@click="onSelect(option.value)"
					>
						<img v-if="option.iconUrl" :src="option.iconUrl" :alt="option.label" class="size-5" />
						<span>{{ option.label }}</span>
					</button>
				</li>
			</ul>
		</Transition>
	</div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
	transition:
		opacity 0.15s ease,
		transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(4px);
}
</style>
